import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { Phone, Mail, MapPin, Shield, ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useSettings } from "@/lib/settings";

export default function Footer() {
  const { settings } = useSettings();
  const [policy, setPolicy] = useState(null);

  const policies = [
    { key: "privacy_policy", label: "Privacy policy" },
    { key: "terms_of_service", label: "Terms of service" },
    { key: "refund_policy", label: "Refund policy" },
  ].filter(p => settings?.[p.key]);

  return (
    <footer className="relative border-t border-border bg-secondary/40">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-eagle-gold/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2 space-y-4">
          <Logo size={32} />
          <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
            Brisbane&apos;s trusted alarm monitoring and security services. We supply, install and monitor
            CCTV &amp; alarm systems for residential and commercial properties across Queensland.
          </p>
          <div className="inline-flex items-center gap-2 text-xs text-muted-foreground px-3 py-1.5 rounded-full border border-border bg-card">
            <Shield className="h-3 w-3 text-eagle-gold" />
            <span>Licensed · Insured · 24/7 Response</span>
          </div>
        </div>
        <div>
          <div className="font-display font-semibold mb-4 text-sm uppercase tracking-wider">Services</div>
          <ul className="space-y-2.5 text-sm text-muted-foreground">
            <li><Link to="/products?category=camera" className="group inline-flex items-center gap-1 hover:text-eagle-gold transition-colors">CCTV Cameras<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" /></Link></li>
            <li><Link to="/products?category=accessory" className="group inline-flex items-center gap-1 hover:text-eagle-gold transition-colors">Intercom Services<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" /></Link></li>
            <li><Link to="/products?category=alarm" className="group inline-flex items-center gap-1 hover:text-eagle-gold transition-colors">Alarm Systems<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" /></Link></li>
            <li><Link to="/about" className="group inline-flex items-center gap-1 hover:text-eagle-gold transition-colors">About Mohammad<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" /></Link></li>
            <li><Link to="/quote" className="group inline-flex items-center gap-1 hover:text-eagle-gold transition-colors">Residential Install<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" /></Link></li>
            <li><Link to="/quote" className="group inline-flex items-center gap-1 hover:text-eagle-gold transition-colors">Commercial Install<ArrowUpRight className="h-3 w-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" /></Link></li>
          </ul>
        </div>
        <div>
          <div className="font-display font-semibold mb-4 text-sm uppercase tracking-wider">Contact</div>
          <ul className="space-y-3.5 text-sm text-muted-foreground">
            <li className="flex items-start gap-2.5"><MapPin className="h-4 w-4 mt-0.5 text-eagle-gold shrink-0" /> {settings?.business_address || "Brisbane, QLD, Australia"}</li>
            <li className="flex items-start gap-2.5"><Phone className="h-4 w-4 mt-0.5 text-eagle-gold shrink-0" /> {settings?.business_phone || "+61 400 000 000"}</li>
            <li className="flex items-start gap-2.5"><Mail className="h-4 w-4 mt-0.5 text-eagle-gold shrink-0" /> {settings?.business_email || "info@eagleeye.com.au"}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-border/70">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} Eagle Eye Alarm Monitoring &amp; Security Services</div>
          <div className="flex items-center gap-5 flex-wrap">
            {policies.map(p => (
              <button key={p.key} onClick={() => setPolicy(p)} className="hover:text-eagle-gold transition-colors" data-testid={`footer-${p.key}`}>{p.label}</button>
            ))}
            <Link to="/admin/login" className="hover:text-eagle-gold transition-colors" data-testid="footer-admin-login">Staff Login</Link>
          </div>
        </div>
      </div>

      <Dialog open={!!policy} onOpenChange={(o) => !o && setPolicy(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {policy && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{policy.label}</DialogTitle></DialogHeader>
              <div className="text-sm whitespace-pre-wrap leading-relaxed text-muted-foreground">{settings[policy.key]}</div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </footer>
  );
}